import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export type StreamErrorKind =
  | "unsupported"
  | "permission"
  | "not_found"
  | "overconstrained"
  | "invalid_constraints"
  | "unknown";

export interface StreamError {
  kind: StreamErrorKind;
  message: string;
  cause?: unknown;
}

export interface BrowserSupport {
  supported: boolean;
  error?: string;
}

export interface StartStreamOverrides {
  videoConstraints?: MediaTrackConstraints | false;
  audioConstraints?: MediaTrackConstraints | false;
}

export type TrackStatus = "ended" | "muted" | "unmuted";

export interface TrackStatusInfo {
  kind: "audio" | "video";
  status: TrackStatus;
}

export interface MediaTrackSummary {
  count: number;
  live: boolean;
  muted: boolean | null;
}

export interface TrackSummary {
  audio: MediaTrackSummary;
  video: MediaTrackSummary;
}

/**
 * The device behind an audio track, and the settings the track ended up with.
 */
export interface AudioDeviceSnapshot {
  label: string | null;
  settings: MediaTrackSettings | null;
}

export interface UseMediaStreamOptions {
  /** Video track constraints. Pass `false` to omit the video track. */
  videoConstraints: MediaTrackConstraints | false;
  /** Audio track constraints. Pass `false` to omit the audio track. */
  audioConstraints: MediaTrackConstraints | false;
  /**
   * Start the stream automatically. If `videoConstraints` is set, waits for a
   * video element to be attached via `videoRef` before starting; otherwise
   * starts on mount.
   */
  autoStart?: boolean;
  onStreamStarted?: (stream: MediaStream) => void;
  onStreamStopped?: () => void;
  onStreamError?: (error: StreamError) => void;
  /**
   * Fired when a track on the active stream ends, mutes, or unmutes after
   * acquisition — the lifecycle events `getUserMedia` does not surface.
   */
  onTrackStatusChange?: (info: TrackStatusInfo) => void;
}

/**
 * Outcome of an audio-recovery attempt.
 */
export interface AudioRecoveryResult {
  /** `ok` is true if the recovery attempt was successful or benign. */
  ok: boolean;
  /**
   * Label of the recovered microphone; is non-null only if a new track was
   * successfully swapped in.
   * */
  label: string | null;
  /** Error message if the recovery attempt failed. */
  error: string | null;
}

export interface UseMediaStreamReturn {
  stream: MediaStream | null;
  videoRef: (node: HTMLVideoElement | null) => void;
  isVideoElementReady: boolean;
  isStartingCamera: boolean;
  browserSupport: BrowserSupport;
  /**
   * Request a new MediaStream. If a stream is currently active it is stopped
   * first. Pass overrides to start with constraints different from the props
   * (e.g. for a Meet-style camera-off renegotiation).
   */
  startStream: (overrides?: StartStreamOverrides) => Promise<void>;
  /** Stop the active stream and detach it from the video element. */
  stopStream: () => void;
  /** Toggle `enabled` on every video track of the active stream. */
  setVideoEnabled: (enabled: boolean) => void;
  /** Toggle `enabled` on every audio track of the active stream. */
  setAudioEnabled: (enabled: boolean) => void;
  /**
   * Replace the active stream's audio track by re-acquiring a microphone with
   * the given constraints (the current default when omitted), keeping the
   * existing video track(s).
   */
  recoverAudioTrack: (
    audioConstraints?: MediaTrackConstraints | false
  ) => Promise<AudioRecoveryResult>;
}

const UNSUPPORTED_MESSAGE =
  "Your browser doesn't support camera access. Please use a modern browser like Chrome, Firefox, Safari, or Edge.";

export function checkGetUserMediaSupport(): BrowserSupport {
  if (navigator.mediaDevices?.getUserMedia == null) {
    return { supported: false, error: UNSUPPORTED_MESSAGE };
  }
  return { supported: true };
}

function mapGetUserMediaError(error: unknown): StreamError {
  if (!(error instanceof Error)) {
    return {
      kind: "unknown",
      message: "Failed to access media devices.",
      cause: error,
    };
  }
  switch (error.name) {
    case "NotAllowedError":
    case "SecurityError":
      return {
        kind: "permission",
        message:
          "Permission to access the camera or microphone was denied. Please grant access and try again.",
        cause: error,
      };
    case "NotFoundError":
    case "DevicesNotFoundError":
      return {
        kind: "not_found",
        message: "No camera or microphone was found on this device.",
        cause: error,
      };
    case "OverconstrainedError":
    case "ConstraintNotSatisfiedError":
      return {
        kind: "overconstrained",
        message:
          "The requested camera or microphone could not be used with these settings.",
        cause: error,
      };
    case "TypeError":
      return {
        kind: "invalid_constraints",
        message: "Invalid camera or microphone constraints.",
        cause: error,
      };
    default:
      return {
        kind: "unknown",
        message: error.message || "Failed to access media devices.",
        cause: error,
      };
  }
}

function buildConstraints(
  videoConstraints: MediaTrackConstraints | false,
  audioConstraints: MediaTrackConstraints | false
): MediaStreamConstraints | null {
  if (videoConstraints === false && audioConstraints === false) return null;
  return {
    video: videoConstraints,
    audio: audioConstraints,
  };
}

// Errors a vanished exact-deviceId constraint produces; mirrors the device
// cases in mapGetUserMediaError.
function isUnsatisfiableDeviceError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  switch (error.name) {
    case "OverconstrainedError":
    case "ConstraintNotSatisfiedError":
    case "NotFoundError":
    case "DevicesNotFoundError":
      return true;
    default:
      return false;
  }
}

function hasExactDeviceId(constraints: MediaTrackConstraints | false): boolean {
  if (constraints === false) return false;
  const { deviceId } = constraints;
  return (
    typeof deviceId === "object" && deviceId !== null && "exact" in deviceId
  );
}

function withoutDeviceId(
  constraints: MediaTrackConstraints | false
): MediaTrackConstraints | false {
  if (constraints === false) return false;
  const { deviceId: _deviceId, ...rest } = constraints;
  return rest;
}

/**
 * Acquire a stream, falling back to the system default when an explicitly
 * requested device is gone. The stored device is a preference, not a guarantee
 * it still exists
 */
async function acquireWithFallback(
  constraints: MediaStreamConstraints,
  video: MediaTrackConstraints | false,
  audio: MediaTrackConstraints | false
): Promise<MediaStream> {
  try {
    return await navigator.mediaDevices.getUserMedia(constraints);
  } catch (error) {
    const requestedExactDevice =
      hasExactDeviceId(video) || hasExactDeviceId(audio);
    if (!isUnsatisfiableDeviceError(error) || !requestedExactDevice) {
      throw error;
    }

    // Retry without the exact deviceId so the browser selects the default.
    const relaxed = buildConstraints(
      withoutDeviceId(video),
      withoutDeviceId(audio)
    );
    if (relaxed == null) throw error;
    return navigator.mediaDevices.getUserMedia(relaxed);
  }
}

function summarizeKind(tracks: MediaStreamTrack[]): MediaTrackSummary {
  return {
    count: tracks.length,
    live: tracks.some((track) => track.readyState === "live"),
    muted: tracks[0]?.muted ?? null,
  };
}

/**
 * Names the device behind a stream's audio track.
 */
export function describeAudioDevice(
  stream: MediaStream | null
): AudioDeviceSnapshot {
  const track = stream?.getAudioTracks()[0];
  if (track == null) return { label: null, settings: null };

  // label is not part of getSettings(), so it is read off the track itself.
  return { label: track.label || null, settings: track.getSettings() };
}

/** Snapshot of the audio/video tracks on a stream, for diagnostics. */
export function summarizeTracks(stream: MediaStream | null): TrackSummary {
  return {
    audio: summarizeKind(stream?.getAudioTracks() ?? []),
    video: summarizeKind(stream?.getVideoTracks() ?? []),
  };
}

/**
 * Listen for end/mute/unmute on every track of a stream. Returns a detach
 * function that removes all listeners.
 */
function attachTrackListeners(
  stream: MediaStream,
  notify: (info: TrackStatusInfo) => void
): () => void {
  const detachers = stream.getTracks().map((track) => {
    const kind: "audio" | "video" = track.kind === "audio" ? "audio" : "video";
    const onEnded = () => notify({ kind, status: "ended" });
    const onMute = () => notify({ kind, status: "muted" });
    const onUnmute = () => notify({ kind, status: "unmuted" });
    track.addEventListener("ended", onEnded);
    track.addEventListener("mute", onMute);
    track.addEventListener("unmute", onUnmute);
    return () => {
      track.removeEventListener("ended", onEnded);
      track.removeEventListener("mute", onMute);
      track.removeEventListener("unmute", onUnmute);
    };
  });
  return () => detachers.forEach((detach) => detach());
}

function stopAllTracks(stream: MediaStream | null): void {
  if (!stream) return;
  stream.getTracks().forEach((track) => {
    try {
      track.stop();
    } catch {
      // already stopped
    }
  });
}

function setTracksEnabled(
  stream: MediaStream | null,
  kind: "video" | "audio",
  enabled: boolean
): void {
  if (!stream) return;
  const tracks =
    kind === "video" ? stream.getVideoTracks() : stream.getAudioTracks();
  tracks.forEach((track) => {
    track.enabled = enabled;
  });
}

/**
 * Low-level hook around `navigator.mediaDevices.getUserMedia`.
 *
 * Owns the MediaStream lifecycle and binds it to a `<video>` element via a
 * ref callback. Knows nothing about photo capture, MediaRecorder, or capture
 * modes — those concerns live in higher layers.
 *
 * Errors are reported via the `onStreamError` callback as a typed
 * `{ kind, message }` object; the hook never calls `alert()` or throws past
 * the caller.
 */
export function useMediaStream({
  videoConstraints,
  audioConstraints,
  autoStart = false,
  onStreamStarted,
  onStreamStopped,
  onStreamError,
  onTrackStatusChange,
}: UseMediaStreamOptions): UseMediaStreamReturn {
  const browserSupport = useMemo(() => checkGetUserMediaSupport(), []);

  const [stream, setStream] = useState<MediaStream | null>(null);
  const [isVideoElementReady, setIsVideoElementReady] = useState(false);
  const [isStartingCamera, setIsStartingCamera] = useState(false);

  const streamRef = useRef<MediaStream | null>(null);
  const videoElementRef = useRef<HTMLVideoElement | null>(null);
  const mountedRef = useRef(true);
  // Monotonic id so overlapping startStream calls can drop stale resolutions.
  const requestIdRef = useRef(0);
  // Guards against overlapping audio-recovery attempts.
  const recoveringRef = useRef(false);
  // Detaches the track listeners for the currently active stream.
  const detachTrackListenersRef = useRef<(() => void) | null>(null);

  // Always-fresh refs so async closures and toggles read the latest values
  // without needing to be re-created on every render.
  const callbacksRef = useRef({
    onStreamStarted,
    onStreamStopped,
    onStreamError,
    onTrackStatusChange,
  });
  useEffect(() => {
    callbacksRef.current = {
      onStreamStarted,
      onStreamStopped,
      onStreamError,
      onTrackStatusChange,
    };
  });

  const constraintsRef = useRef({ videoConstraints, audioConstraints });
  useEffect(() => {
    constraintsRef.current = { videoConstraints, audioConstraints };
  });

  const detachVideoElement = useCallback((node: HTMLVideoElement) => {
    node.srcObject = null;
    try {
      node.pause();
    } catch {
      // already paused
    }
    // Force a fresh load so the browser releases internal references to the
    // previous stream (matches the existing aggressive-cleanup behaviour).
    node.load();
  }, []);

  const videoRef = useCallback(
    (node: HTMLVideoElement | null) => {
      if (!node && videoElementRef.current) {
        detachVideoElement(videoElementRef.current);
      }
      videoElementRef.current = node;
      setIsVideoElementReady(!!node);
      // Attach an already-active stream so callers can mount the element
      // after starting the stream.
      if (node && streamRef.current) {
        node.srcObject = streamRef.current;
      }
    },
    [detachVideoElement]
  );

  const releaseCurrentStream = useCallback(() => {
    if (!streamRef.current) return;
    detachTrackListenersRef.current?.();
    detachTrackListenersRef.current = null;
    stopAllTracks(streamRef.current);
    streamRef.current = null;
    setStream(null);
    callbacksRef.current.onStreamStopped?.();
  }, []);

  const stopStream = useCallback(() => {
    if (videoElementRef.current) {
      videoElementRef.current.srcObject = null;
      try {
        videoElementRef.current.pause();
      } catch {
        // already paused
      }
      videoElementRef.current.load();
    }
    releaseCurrentStream();
  }, [releaseCurrentStream]);

  const startStream = useCallback(async (overrides?: StartStreamOverrides) => {
    if (!mountedRef.current) return;

    const support = checkGetUserMediaSupport();
    if (!support.supported) {
      callbacksRef.current.onStreamError?.({
        kind: "unsupported",
        message: support.error ?? UNSUPPORTED_MESSAGE,
      });
      return;
    }

    const effectiveVideo =
      overrides?.videoConstraints ?? constraintsRef.current.videoConstraints;
    const effectiveAudio =
      overrides?.audioConstraints ?? constraintsRef.current.audioConstraints;
    const constraints = buildConstraints(effectiveVideo, effectiveAudio);

    if (!constraints) {
      callbacksRef.current.onStreamError?.({
        kind: "overconstrained",
        message:
          "Cannot start a media stream with both video and audio disabled.",
      });
      return;
    }

    setIsStartingCamera(true);

    // Stop any existing stream before requesting a new one to avoid the
    // browser racing two getUserMedia sessions on the same device.
    if (streamRef.current) {
      detachTrackListenersRef.current?.();
      detachTrackListenersRef.current = null;
      stopAllTracks(streamRef.current);
      streamRef.current = null;
      setStream(null);
      callbacksRef.current.onStreamStopped?.();
    }

    requestIdRef.current += 1;
    const myRequestId = requestIdRef.current;

    try {
      const newStream = await acquireWithFallback(
        constraints,
        effectiveVideo,
        effectiveAudio
      );

      // A later startStream call has superseded this one — drop the stale
      // stream so its tracks do not leak in the background.
      if (!mountedRef.current || myRequestId !== requestIdRef.current) {
        stopAllTracks(newStream);
        return;
      }

      streamRef.current = newStream;
      setStream(newStream);
      detachTrackListenersRef.current = attachTrackListeners(
        newStream,
        (info) => callbacksRef.current.onTrackStatusChange?.(info)
      );
      if (videoElementRef.current) {
        videoElementRef.current.srcObject = newStream;
      }
      callbacksRef.current.onStreamStarted?.(newStream);
    } catch (error) {
      if (!mountedRef.current || myRequestId !== requestIdRef.current) return;
      console.error("getUserMedia failed", error);
      callbacksRef.current.onStreamError?.(mapGetUserMediaError(error));
    } finally {
      if (mountedRef.current && myRequestId === requestIdRef.current) {
        setIsStartingCamera(false);
      }
    }
  }, []);

  const setVideoEnabled = useCallback((enabled: boolean) => {
    setTracksEnabled(streamRef.current, "video", enabled);
  }, []);

  const setAudioEnabled = useCallback((enabled: boolean) => {
    setTracksEnabled(streamRef.current, "audio", enabled);
  }, []);

  const recoverAudioTrack = async (
    audioConstraints?: MediaTrackConstraints | false
  ): Promise<AudioRecoveryResult> => {
    const current = streamRef.current;
    // No active stream, a recovery already in flight, or audio explicitly
    // not requested — nothing to do, but not a failure.
    if (
      current == null ||
      recoveringRef.current ||
      audioConstraints === false
    ) {
      return { ok: true, error: null, label: null };
    }

    recoveringRef.current = true;
    try {
      const replacement = await navigator.mediaDevices.getUserMedia({
        audio: audioConstraints ?? true,
      });
      const newTrack = replacement.getAudioTracks()[0] ?? null;

      // No microphone available to fall back to - fail.
      if (newTrack == null) {
        stopAllTracks(replacement);
        return {
          ok: false,
          error: "No microphone available to recover.",
          label: null,
        };
      }

      // Unmounted or superseded by a full restart while the request was in
      // flight — drop the replacement
      if (!mountedRef.current || streamRef.current !== current) {
        stopAllTracks(replacement);
        return { ok: true, error: null, label: null };
      }

      // Rebuild the active stream: keep the live video track(s), swap in the
      // recovered audio track. A fresh MediaStream identity lets consumers
      // (volume meter, audio graph) react to the swap.
      detachTrackListenersRef.current?.();
      current.getAudioTracks().forEach((track) => {
        try {
          track.stop();
        } catch {
          // already stopped
        }
      });

      const refreshed = new MediaStream([
        ...current.getVideoTracks(),
        newTrack,
      ]);
      streamRef.current = refreshed;
      setStream(refreshed);
      detachTrackListenersRef.current = attachTrackListeners(
        refreshed,
        (info) => callbacksRef.current.onTrackStatusChange?.(info)
      );
      if (videoElementRef.current) {
        videoElementRef.current.srcObject = refreshed;
      }

      return {
        ok: true,
        error: null,
        label: newTrack.label,
      };
    } catch (error) {
      console.error("Audio recovery failed", error);
      return {
        ok: false,
        error:
          error instanceof Error ? error.message : "Audio recovery failed.",
        label: null,
      };
    } finally {
      recoveringRef.current = false;
    }
  };

  // Auto-start. Waits for a video element only if video is requested — an
  // audio-only stream needs no preview surface.
  useEffect(() => {
    if (!autoStart) return;
    if (streamRef.current) return;

    const needsVideoElement = constraintsRef.current.videoConstraints !== false;
    if (needsVideoElement && !isVideoElementReady) return;

    void startStream();
  }, [autoStart, isVideoElementReady, startStream]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      detachTrackListenersRef.current?.();
      detachTrackListenersRef.current = null;
      if (streamRef.current) {
        stopAllTracks(streamRef.current);
        streamRef.current = null;
      }
      if (videoElementRef.current) {
        videoElementRef.current.srcObject = null;
      }
    };
  }, []);

  return {
    stream,
    videoRef,
    isVideoElementReady,
    isStartingCamera,
    browserSupport,
    startStream,
    stopStream,
    setVideoEnabled,
    setAudioEnabled,
    recoverAudioTrack,
  };
}
